import React, { useEffect, useState, useCallback } from "react";
import { useVenue } from "@/components/shared/VenueContext";
import { hosysAiApi } from "@/api/hosysai";
import HosysAILayout from "@/components/hosysai/HosysAILayout";
import { toast } from "sonner";
import { Plus, Trash2, Brain, X, Loader2, ShieldCheck } from "lucide-react";

const RISK_STYLE = {
  low:              { dot: "bg-emerald-500", label: "Baix",              badge: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" },
  moderate:         { dot: "bg-amber-500",   label: "Moderat",           badge: "bg-amber-500/10 text-amber-300 border-amber-500/30" },
  review_required:  { dot: "bg-orange-500",  label: "Requereix revisió", badge: "bg-orange-500/10 text-orange-300 border-orange-500/30" },
  high:             { dot: "bg-red-500",     label: "Risc important",    badge: "bg-red-500/10 text-red-300 border-red-500/30" },
};

const EMPTY_FORM = {
  name: "", provider: "", model: "", version: "", purpose: "", department: "",
  responsible: "", observations: "", manual_risk: "",
  uses_personal_data: false, uses_sensitive_data: false,
  automated_decisions: false, used_in_hr: false, human_supervision: true,
};

function BoolField({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
      <span className="text-sm text-slate-200">{label}</span>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="accent-cyan-400" data-testid={testId} />
    </label>
  );
}

function InputField({ label, value, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="text-xs text-slate-400 uppercase tracking-wider">{label}</label>
      <input
        value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-cyan-400"
        data-testid={testId}
      />
    </div>
  );
}

export default function HosysAISystems() {
  const { currentVenue, loading: venueLoading } = useVenue();
  const venueId = currentVenue?.venue_id || currentVenue?.id;
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    try {
      const items = await hosysAiApi.listSystems(venueId);
      setSystems(items || []);
    } catch (e) { /* noop */ } finally {
      setLoading(false);
    }
  }, [venueId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || "", provider: s.provider || "", model: s.model || "",
      version: s.version || "", purpose: s.purpose || "", department: s.department || "",
      responsible: s.responsible || "", observations: s.observations || "", manual_risk: s.manual_risk || "",
      uses_personal_data: !!s.uses_personal_data, uses_sensitive_data: !!s.uses_sensitive_data,
      automated_decisions: !!s.automated_decisions, used_in_hr: !!s.used_in_hr,
      human_supervision: s.human_supervision !== false,
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("El nom és obligatori"); return; }
    setSaving(true);
    try {
      if (editing) {
        await hosysAiApi.updateSystem(editing.ai_system_id, form);
        toast.success("Sistema actualitzat");
      } else {
        await hosysAiApi.createSystem({ venue_id: venueId, ...form });
        toast.success("Sistema afegit");
      }
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.message || "Error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar aquest sistema?")) return;
    try {
      await hosysAiApi.deleteSystem(id);
      toast.success("Eliminat");
      load();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <HosysAILayout>
      <div className="space-y-6" data-testid="hosysai-systems-page">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4 justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-cyan-300 font-semibold">Inventari IA</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mt-1">Sistemes d'IA</h1>
            <p className="text-slate-400 text-sm mt-1">Registra cada eina o sistema d'IA en ús a la teva organització i el seu nivell de risc.</p>
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white font-semibold flex items-center gap-2 text-sm"
            data-testid="systems-add-btn"
          >
            <Plus className="w-4 h-4" /> Nou sistema
          </button>
        </div>

        {loading || venueLoading ? (
          <div className="h-40 flex items-center justify-center text-slate-400">Carregant…</div>
        ) : systems.length === 0 ? (
          <div className="p-8 text-center rounded-2xl border border-white/10 bg-white/5">
            <Brain className="w-12 h-12 mx-auto text-slate-500 mb-3" />
            <p className="text-white font-semibold">Cap sistema registrat</p>
            <p className="text-sm text-slate-400 mt-1">Registra el primer sistema d'IA per començar l'inventari.</p>
          </div>
        ) : (
          <>
            <div className="p-3 rounded-xl border border-slate-700/50 bg-slate-900/40 text-xs text-slate-400 flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-cyan-400 flex-shrink-0 mt-0.5" />
              La classificació és orientativa i no substitueix una avaluació jurídica o tècnica específica.
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {systems.map((s) => {
                const risk = RISK_STYLE[s.risk_level] || RISK_STYLE.low;
                return (
                  <div key={s.ai_system_id} className="p-4 rounded-2xl border border-white/10 bg-white/5 hover:border-cyan-400/30 transition-colors cursor-pointer"
                    onClick={() => openEdit(s)} data-testid={`system-card-${s.ai_system_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-white truncate">{s.name}</p>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold rounded-full border ${risk.badge}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${risk.dot}`} /> {risk.label}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5 truncate">
                          {s.provider || "—"}{s.model ? ` · ${s.model}` : ""}{s.version ? ` v${s.version}` : ""}
                        </p>
                        {s.purpose && <p className="text-sm text-slate-300 mt-2 line-clamp-2">{s.purpose}</p>}
                        <div className="flex flex-wrap gap-1 mt-3">
                          {s.uses_personal_data && <span className="text-[10px] px-2 py-0.5 rounded-full bg-fuchsia-500/10 text-fuchsia-300 border border-fuchsia-500/30">Dades personals</span>}
                          {s.uses_sensitive_data && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-300 border border-red-500/30">Dades sensibles</span>}
                          {s.automated_decisions && <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-300 border border-orange-500/30">Decisions automàtiques</span>}
                          {s.used_in_hr && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">RRHH</span>}
                          {!s.human_supervision && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-300 border border-red-500/30">Sense supervisió</span>}
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); remove(s.ai_system_id); }}
                        className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-400"
                        data-testid={`system-delete-${s.ai_system_id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Modal */}
        {showForm && (
          <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-slate-900/95 backdrop-blur border-b border-white/10 px-5 py-3 flex items-center justify-between z-10">
                <p className="font-semibold text-white">{editing ? "Editar sistema" : "Nou sistema d'IA"}</p>
                <button onClick={() => setShowForm(false)} className="p-1.5 rounded-lg hover:bg-white/10" data-testid="system-form-close">
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <InputField label="Nom *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Ex: ChatGPT" testId="system-name" />
                  <InputField label="Proveïdor" value={form.provider} onChange={(v) => setForm({ ...form, provider: v })} placeholder="Ex: OpenAI" testId="system-provider" />
                  <InputField label="Model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} placeholder="Ex: GPT-4o" />
                  <InputField label="Versió" value={form.version} onChange={(v) => setForm({ ...form, version: v })} placeholder="Ex: 4.0" />
                  <InputField label="Departament" value={form.department} onChange={(v) => setForm({ ...form, department: v })} placeholder="Ex: Màrqueting" />
                  <InputField label="Responsable" value={form.responsible} onChange={(v) => setForm({ ...form, responsible: v })} placeholder="Nom del responsable" />
                </div>
                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Finalitat / Ús</label>
                  <textarea
                    rows={2} value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-cyan-400"
                    placeholder="Descriu com i per què s'utilitza aquest sistema"
                    data-testid="system-purpose"
                  />
                </div>

                <div>
                  <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Característiques de risc</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    <BoolField label="Utilitza dades personals" value={form.uses_personal_data} onChange={(v) => setForm({ ...form, uses_personal_data: v })} testId="system-personal" />
                    <BoolField label="Utilitza dades sensibles" value={form.uses_sensitive_data} onChange={(v) => setForm({ ...form, uses_sensitive_data: v })} />
                    <BoolField label="Decisions automatitzades" value={form.automated_decisions} onChange={(v) => setForm({ ...form, automated_decisions: v })} />
                    <BoolField label="Ús en RRHH" value={form.used_in_hr} onChange={(v) => setForm({ ...form, used_in_hr: v })} />
                    <BoolField label="Supervisió humana" value={form.human_supervision} onChange={(v) => setForm({ ...form, human_supervision: v })} testId="system-supervision" />
                  </div>
                </div>

                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Sobreescriure risc (opcional)</label>
                  <select
                    value={form.manual_risk} onChange={(e) => setForm({ ...form, manual_risk: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-cyan-400"
                  >
                    <option value="">Auto-classificació</option>
                    <option value="low">Baix</option>
                    <option value="moderate">Moderat</option>
                    <option value="review_required">Requereix revisió</option>
                    <option value="high">Risc important</option>
                  </select>
                </div>

                <div>
                  <label className="text-xs text-slate-400 uppercase tracking-wider">Observacions</label>
                  <textarea
                    rows={3} value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })}
                    className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-cyan-400"
                    placeholder="Mesures de mitigació, controls, etc."
                  />
                </div>
              </div>
              <div className="sticky bottom-0 bg-slate-900/95 backdrop-blur border-t border-white/10 px-5 py-3 flex gap-2 justify-end">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg text-slate-300 hover:bg-white/5 text-sm">Cancel·lar</button>
                <button
                  onClick={submit} disabled={saving}
                  className="px-5 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-fuchsia-500 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                  data-testid="system-form-submit"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </HosysAILayout>
  );
}
